import { differenceInMonths, format, isDate, isSameDay } from "date-fns";
import type { FetcherWithComponents } from "react-router";
import { toast } from "react-toastify";
import * as SentryB from "@sentry/browser";

export const SES_SEND_EMAIL_URL =
  "https://email.us-east-2.amazonaws.com/v2/email/outbound-emails";

export const createDateObject = (dateValOrString: string, isUtc?: boolean) => {
  // 2017-09-07T13:30:16.01
  let output: Date | null = null;
  let dateString;
  const dateFormat = RegExp(/\d{1,2}\/\d{1,2}\/\d{4}/);
  const dateFormat2 = RegExp(/\d{4}-\d{2}-\d{2}/);
  let parsed = false;

  let year = 0;
  let month = 0;
  let day = 0;

  let hours = 0;
  let minutes = 0;

  if (isDate(dateValOrString)) {
    /// 2000-01-01T05:00:00.000Z
    dateString = format(
      dateValOrString as unknown as Date | number,
      "yyyy-MM-dd'T'HH:mm:ss",
    );
  } else {
    dateString = dateValOrString;
  }

  if (!dateString) {
    return output;
  }

  if (dateString.includes("T")) {
    const [datePart, rest] = dateString.split("T");
    const dParts = datePart.split("-");
    year = +dParts[0];
    month = +dParts[1] - 1;
    day = +dParts[2];

    const tParts = rest.split(":");
    hours = +tParts[0];
    minutes = +tParts[1];
    parsed = true;
  } else if (dateString.includes(" ")) {
    // '2025-01-31 12:13:00'
    // Postgres replaces the 'T' with a space no matter what is sent
    const [datePart, rest] = dateString.split(" ");
    const dParts = datePart.split("-");
    year = +dParts[0];
    month = +dParts[1] - 1;
    day = +dParts[2];

    const tParts = rest.split(":");
    hours = +tParts[0];
    minutes = +tParts[1];
    parsed = true;
  } else if (dateFormat.test(dateString)) {
    const dParts = dateString.split("/");
    month = +dParts[0] - 1;
    day = +dParts[1];
    year = +dParts[2];
    parsed = true;
  } else if (dateFormat2.test(dateString)) {
    const dParts = dateString.split("-");
    year = +dParts[0];
    month = +dParts[1] - 1;
    day = +dParts[2];
    parsed = true;
  }
  if (parsed) {
    if (isUtc) {
      output = new Date(Date.UTC(year, month, day, hours, minutes));
    } else {
      output = new Date(year, month, day, hours, minutes);
    }
  }

  return output;
};

export const formatDateForDOB = (date: string | null) => {
  if (!date) {
    return "";
  }
  const dateObj = createDateObject(date);
  return format(dateObj!, "yyyy-MM-dd");
};

export const formatDate = (date: string | null) => {
  if (!date) {
    return "";
  }
  const dateObj = createDateObject(date);
  return format(dateObj!, "MM/dd/yyyy");
};
export const formatDateWithTime = (date: string | null) => {
  if (!date) {
    return "";
  }
  const dateObj = createDateObject(date);
  if (!dateObj) {
    return "";
  }

  return format(dateObj!, "MM/dd/yyyy hh:mm a");
};
export const formatDateOnlyTime = (date: string | null) => {
  if (!date) {
    return "";
  }
  const dateObj = createDateObject(date);
  return format(dateObj!, "hh:mm a");
};

export const formatDateForFileName = (date: string | null) => {
  if (!date) {
    return "";
  }
  const dateObj = createDateObject(date);
  if (!dateObj) {
    return "";
  }

  return format(dateObj!, "MM-dd-yyyy");
};

export const isStartAndEndAndEventSameDay = (
  start: string,
  end: string,
  event: string,
) => {
  if (event && !start && !end) {
    return true; // might as well be same day
  }
  if (start && end && event) {
    const startObj = createDateObject(start);
    const endObj = createDateObject(end);
    const eventObj = createDateObject(event);

    const isStartAndEndSame = isSameDay(
      new Date(startObj ?? ""),
      new Date(endObj ?? ""),
    );
    if (!isStartAndEndSame) {
      return false;
    }
    return isSameDay(new Date(startObj ?? ""), new Date(eventObj ?? ""));
  }
  if (event && start && !end) {
    const startObj = createDateObject(start);
    const eventObj = createDateObject(event);

    return isSameDay(new Date(startObj ?? ""), new Date(eventObj ?? ""));
  }
  if (event && !start && end) {
    const endObj = createDateObject(end);
    const eventObj = createDateObject(event);

    return isSameDay(new Date(endObj ?? ""), new Date(eventObj ?? ""));
  }
};

export const getAge = (dateOfBirth: string) => {
  const months = Math.abs(
    differenceInMonths(new Date(dateOfBirth), new Date()),
  );
  const years = Math.floor(months / 12);
  const remainderMonths = months % 12;

  return `${years} yrs ${remainderMonths} mths`;
};

export const scrollIntoView = (id: string) => {
  if (document.getElementById(id) != null) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }
};

export const debounce = (fn: Function, ms = 300) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

export const resetFetcher = (fetcher: FetcherWithComponents<any>) => {
  fetcher.submit({}, { action: "/reset-fetcher", method: "post" });
};

export function ToastFunction(
  message?: string,
  type?: string,
  toastId?: string,
  onClose?: () => void,
) {
  if (message) {
    switch (type) {
      case "error":
        return toast.error(message, {
          toastId: toastId ?? "",
          onClose: onClose,
        });
      case "info":
        return toast.info(message, { toastId: toastId ?? "" });
      case "success":
        return toast.success(message, { toastId: toastId ?? "" });
      default:
        break;
    }
  }
}

export const base64ToBlob = (
  b64Data: string,
  contentType = "",
  sliceSize = 512,
) => {
  const byteCharacters = atob(b64Data);
  // const byteCharacters = btoa(unescape(encodeURIComponent(b64Data)));
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

export const sortGroupMembersByPersonLastName = (a: any, b: any) => {
  if ("person" in a && "person" in b) {
    const aName = a.person.lastName?.toLowerCase() ?? "";
    const bName = b.person.lastName?.toLowerCase() ?? "";
    if (aName < bName) {
      return -1;
    }
    if (aName > bName) {
      return 1;
    }
    return 0;
  } else {
    return 0;
  }
};

type ErrorWithMessage = {
  message: string;
};

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as Record<string, unknown>).message === "string"
  );
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError;

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
}

export const getErrorMessage = (error: unknown) => {
  return toErrorWithMessage(error).message;
};

export const handleErrorClient = async (error: unknown) => {
  SentryB.captureException(error);
  const message = getErrorMessage(error);

  return message;
};

// genders
// optionMethod={(genders: Gender[]) =>
//   genders.map((g) => (
//     <option value={g.genderId} key={g.genderId}>
//       {g.genderName}
//     </option>
//   ))
// }
